/**
 * WorldsView — tree-wide discovery of life capsules.
 *
 * For each capsule that applies to at least one member of the tree, shows:
 *   - Hero card with image / themed gradient fallback
 *   - Member list: avatars + names of the relatives who lived through it
 *
 * Clicking a member opens TheirWorldView scoped to that person + capsule.
 *
 * Empty state: nudges the user to add birth/death places and dates so we
 * can place their relatives into eras.
 */
import { useEffect, useState } from 'react'
import { mediaUrl, api } from '../../services/api.js'
import TheirWorldView from '../person/TheirWorldView.jsx'

const THEME_ICON = {
  war:       '⚔️',
  childhood: '🧒',
  work:      '🔨',
  migration: '🧳',
  epidemic:  '🦠',
  flood:     '🌊',
  other:     '🌍',
}

const THEME_GRADIENT = {
  war:       'linear-gradient(135deg, #3a2520 0%, #6b3c28 100%)',
  childhood: 'linear-gradient(135deg, #2d4a3e 0%, #5a7d65 100%)',
  work:      'linear-gradient(135deg, #2a2a2a 0%, #4a3a2a 100%)',
  migration: 'linear-gradient(135deg, #1e3a5f 0%, #3d5a7f 100%)',
  epidemic:  'linear-gradient(135deg, #2d2a3a 0%, #4a4560 100%)',
  flood:     'linear-gradient(135deg, #1a3a5a 0%, #3a6080 100%)',
  other:     'linear-gradient(135deg, #2a2a2a 0%, #4a4a4a 100%)',
}

export default function WorldsView({ treeId }) {
  const [capsules, setCapsules] = useState(null)
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(null) // { personId, slug }

  useEffect(() => {
    if (!treeId) return
    api.get(`/trees/${treeId}/capsules`)
      .then(setCapsules)
      .catch(err => setError(err.message))
  }, [treeId])

  if (error) return <div className="form-error" style={{ margin: '2rem' }}>{error}</div>
  if (capsules === null) return <div className="loading" style={{ margin: '3rem', textAlign: 'center' }}>Loading…</div>

  if (capsules.length === 0) {
    return (
      <div className="empty-state" style={{ padding: '3rem 1rem' }}>
        <div className="empty-state__icon">🌍</div>
        <h3>No worlds yet</h3>
        <p>
          Add birth dates and places to your tree members — we'll match them
          to the historical periods they lived through and show each of
          those eras as an immersive capsule here.
        </p>
      </div>
    )
  }

  return (
    <div className="worlds-view">
      <div className="worlds-view__header">
        <div className="page-header__label">Worlds</div>
        <h2 className="worlds-view__title">The eras your family touched</h2>
        <p className="worlds-view__hint">
          Every period with at least one relative in it. Click a name to
          step into their version of that world.
        </p>
      </div>

      <div className="worlds-view__grid">
        {capsules.map(c => (
          <CapsuleTile
            key={c.id}
            capsule={c}
            onOpenPerson={(personId) => setOpen({ personId, slug: c.slug })}
          />
        ))}
      </div>

      {open && (
        <TheirWorldView
          personId={open.personId}
          slug={open.slug}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  )
}

function CapsuleTile({ capsule, onOpenPerson }) {
  const [imgOk, setImgOk] = useState(true)
  const gradient = THEME_GRADIENT[capsule.theme] || THEME_GRADIENT.other
  const years = capsule.start_year === capsule.end_year
    ? `${capsule.start_year}`
    : `${capsule.start_year}–${capsule.end_year}`
  const scope = capsule.place || capsule.region || (capsule.country === 'NL' ? 'Netherlands' : 'worldwide')

  return (
    <div className="world-tile">
      <div className="world-tile__hero">
        {capsule.hero_image_url && imgOk ? (
          <img
            className="world-tile__bg"
            src={capsule.hero_image_url}
            alt=""
            aria-hidden
            loading="lazy"
            onError={() => setImgOk(false)}
          />
        ) : (
          <div
            className="world-tile__bg world-tile__bg--fallback"
            style={{ background: gradient }}
            aria-hidden
          />
        )}
        <div className="world-tile__overlay" />
        <div className="world-tile__hero-body">
          <div className="world-tile__icon">{THEME_ICON[capsule.theme] || '🌍'}</div>
          <div className="world-tile__title">{capsule.title}</div>
          {capsule.tagline && <div className="world-tile__tagline">{capsule.tagline}</div>}
          <div className="world-tile__meta">
            {years} · {scope}
          </div>
        </div>
      </div>

      <div className="world-tile__members">
        <div className="world-tile__members-label">
          {capsule.members.length} {capsule.members.length === 1 ? 'relative' : 'relatives'} lived through this
        </div>
        <div className="world-tile__members-list">
          {capsule.members.map(m => (
            <button
              key={m.id}
              className="world-tile__member"
              onClick={() => onOpenPerson(m.id)}
              title="Step into their world"
            >
              <span className="world-tile__avatar">
                {m.photo_thumb_key
                  ? <img src={mediaUrl(m.photo_thumb_key)} alt="" />
                  : (m.preferred_name || m.name)?.[0]?.toUpperCase() || '?'}
              </span>
              <span className="world-tile__member-body">
                <span className="world-tile__member-name">{m.preferred_name || m.name}</span>
                {(m.birth_date || m.death_date) && (
                  <span className="world-tile__member-years">
                    {birthYear(m.birth_date)}{(birthYear(m.birth_date) || deathYear(m.death_date)) && ' – '}
                    {deathYear(m.death_date)}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function birthYear(d) { const m = String(d || '').match(/\b(\d{4})\b/); return m ? m[1] : '' }
function deathYear(d) { const m = String(d || '').match(/\b(\d{4})\b/); return m ? m[1] : '' }
